/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable global-require */
import { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { CustomSelectStyles as customSelectClass } from '../CustomSelect/CustomSelectStyles';
import SearchIcon from '../../../images/search_icon.svg';
import chipDelete from '../../../images/chipDelete.svg';
import './MultiCountrySelectStyles.css';
import LeadGenerationContext from '../../../context/LeadGenerationContext';
import { MultiCountrySelectStyles as styleClass } from './MultiCountrySelectStyles';
import { commonTernary } from '../../commonCode';
import { DownArrowIcon } from '../../../images/svg/CustomizedSVG';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  disableScrollLock: true,
  variant: 'menu',
  disableAutoFocusItem: false,
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  getContentAnchorEl: null,
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP,
      width: 250,
      padding: '0px 5px 5px 5px',
      webkitScrollbar: {
        width: '10px',
      },
    },
  },
};

export default function MultiCountrySelect() {
  const { t } = useTranslation();
  const iRef = useRef();
  const leadGenerationContext = useContext(LeadGenerationContext);
  const {
    productData,
    countries,
    setCountries,
    receivedCountryData,
    setReceivedCountryData,
    countryList,
    setCountryList,
    enterCountry,
    setEnterCountry,
    InternaltionalWireTransferError,
    setInternationalWireTransferError,
    isLightTheme,
    theme,
  } = leadGenerationContext;

  useEffect(() => {
    const list = [];
    // eslint-disable-next-line guard-for-in
    for (const con in productData?.transactionCountry[0]?.countryCodes) {
      list.push({ country: con, code: productData.transactionCountry[0].countryCodes[con] });
    }
    setReceivedCountryData(list);
  }, [productData?.transactionCountry[0]?.countryCodes]);

  useEffect(() => {
    setCountryList(receivedCountryData);
  }, [receivedCountryData]);

  const handleChange = (event) => {
    let {
      target: { value },
    } = event;
    leadGenerationContext.setIsCommercialProductUIDirty(true);

    const value1 = value?.slice(0, value.length - 1);
    const value2 = value?.slice(value.length - 1);
    const temp = value1?.filter((data) => data?.country === value2[0]?.country);
    if (temp?.length > 0) {
      value = value?.filter((data) => data?.country !== temp[0]?.country);
    }

    if (value?.length <= 10) {
      const duplicateRemoved = [];
      value.forEach((item) => {
        if (item?.country) {
          duplicateRemoved.push(item);
        }
      });
      const a = duplicateRemoved.filter(Boolean);
      setCountries(a);
      setInternationalWireTransferError('');
      if (value?.length === 0) {
        setInternationalWireTransferError('* Mandatory Field');
      }
    }
  };

  const removeChip = (code) => {
    leadGenerationContext.setIsCommercialProductUIDirty(true);

    const filteredCountries = countries.filter((ctry) => ctry.code !== code);
    setCountries(filteredCountries);
    if (filteredCountries?.length === 0) {
      setInternationalWireTransferError('* Mandatory Field');
    }
  };

  useEffect(() => {
    if (!enterCountry) {
      setCountryList(receivedCountryData);
    }
  }, [enterCountry]);

  const handleFocus = () => {
    iRef.current.focus();
  };

  const handleChangeSearchChange = (e) => {
    e.stopPropagation();
    setEnterCountry(e.target.value);
  };
  const handleChangeSearch = (e) => {
    e.stopPropagation();
  };

  const handleSearch = (e) => {
    e.stopPropagation();
    leadGenerationContext.setIsCommercialProductUIDirty(true);
    if (!enterCountry) {
      setCountryList(receivedCountryData);
    } else if (enterCountry) {
      const filteredCountries = receivedCountryData.filter((ctry) =>
        ctry.country?.toLowerCase().includes(enterCountry.toLowerCase())
      );
      setCountryList(filteredCountries);
    }
  };

  function cryptoRandom(ctryCode) {
    const uuid = uuidv4().replace(/-/g, '');
    return `${ctryCode}${uuid.slice(0, 4)}`;
  }

  return (
    <div>
      <InputLabel
        data-testid="multiSelect_testid"
        htmlFor="multiCountrySelect"
        aria-label="To which countries do you expect to make or receive your transfer?"
        className={customSelectClass.label_title}
        required
      >
        {t('To which countries do you expect to make or receive your transfer?', {
          defaultValue: 'To which countries do you expect to make or receive your transfer?',
          nsSeparator: '|',
        })}
      </InputLabel>
      <div style={{ position: 'relative' }}>
        {countries.length === 0 && (
          <Typography
            style={{
              position: 'absolute',
              top: InternaltionalWireTransferError ? '20%' : '30%',
              left: '3%',
              opacity: 0.6,
              fontSize: '14px',
            }}
          >
            {t('Select...', {
              defaultValue: 'Select...',
            })}
          </Typography>
        )}
        <Select
          labelId="multiple_checkbox_label"
          id="multiCountrySelect"
          data-testid="multiple_checkbox_testid"
          name="multiCountrySelect"
          multiple
          value={countries}
          onChange={handleChange}
          input={<OutlinedInput className={styleClass.selectOutlinedInp} />}
          renderValue={(selected) =>
            selected
              ?.map((x) =>
                t(x?.country, {
                  defaultValue: x?.country,
                })
              )
              .join(', ')
          }
          MenuProps={{
            disableScrollLock: true,
            ...MenuProps,
            classes: {
              paper: 'paper',
            },
          }}
          displayEmpty
          fullWidth
          IconComponent={() => (
            <Box padding="0 10px 0 0">
              <DownArrowIcon
                style={{ width: 24, height: 24 }}
                color={theme?.palette?.orientalPrimary?.main}
              />
            </Box>
          )}
          classes={{
            icon: customSelectClass.select_icon,
            select: styleClass.select_placeholder,
          }}
          className={commonTernary(
            InternaltionalWireTransferError,
            `${customSelectClass.select_inputs_err}`,
            ''
          )}
          style={{ height: '48px' }}
        >
          <div
            style={{
              position: 'sticky',
              top: 0,
              background: '#ffffff',
              zIndex: 10,
            }}
          >
            <TextField
              inputRef={iRef}
              onChange={handleChangeSearch}
              onBlur={handleChangeSearchChange}
              autoFocus
              placeholder={t('Search', {
                defaultValue: 'Search',
              })}
              style={{ borderBottom: '1px solid #F3F3F3', padding: '10px 0 5px 0', width: '95%' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleSearch}
                      edge="en"
                      aria-label="Search Suri"
                      data-testid="suirSearchIcon_id"
                      classes={{ root: styleClass.iconRoot }}
                    >
                      <img src={SearchIcon} alt="SearchIcon" aria-label="SearchIcon" />
                    </IconButton>
                  </InputAdornment>
                ),
                classes: {
                  input: styleClass.searchField,
                  root: styleClass.inpRoot,
                  notchedOutline: styleClass.searchBox,
                },
                disableUnderline: true,
              }}
            />
            {/* <Typography className={styleClass.selectedCountry}>
              {countries.length > 1 ? 'Selected Countries ' : 'Selected Country '}(
              {countries.length})
            </Typography> */}
            <Typography sx={styleClass.selectedCountry}>
              {t('Selected Countries ', {
                defaultValue: 'Selected Countries ',
              })}
              ({countries.length})
            </Typography>
          </div>
          <div>
            {countries.length > 0 &&
              countries.map(
                (ctry) => (
                  // countries.findIndex((item) => item?.code === ctry?.code) >= 0 && (
                  <Chip
                    className={styleClass.chip}
                    key={cryptoRandom(ctry.code)}
                    label={t(ctry.country, {
                      defaultValue: ctry.country,
                    })}
                    data-testid="deleteChip_testid"
                    onDelete={() => removeChip(ctry.code)}
                    deleteIcon={
                      <img src={chipDelete} alt="delete icon" height="13.5px" width="13.5px" />
                    }
                  />
                )
                // )
              )}
          </div>
          {countryList?.map((ctry, i) => (
            <MenuItem
              tabIndex="-1"
              onFocus={handleFocus}
              data-testid={`${ctry.country}_testid`}
              key={cryptoRandom(ctry.code)}
              value={ctry}
              style={{ padding: 0 }}
            >
              <div style={{ height: '25px', marginBottom: '15px' }}>
                <label className={isLightTheme ? 'checkbox_label' : 'checkbox_label_dark'}>
                  <input
                    type="checkbox"
                    name={ctry.code}
                    onChange={() => {}}
                    checked={countries.findIndex((item) => item?.code === ctry?.code) >= 0}
                    id={ctry.code}
                    data-testid={ctry.code}
                  />
                  <span
                    style={{
                      opacity: 1,
                      marginLeft: '2px',
                      position: 'relative',
                      top: '3px',
                    }}
                    className={
                      isLightTheme
                        ? 'checkbox_custom rectangular'
                        : 'checkbox_custom_dark rectangular'
                    }
                  />

                  <ListItemText
                    className="checkbox_label_style checkbox_content_position"
                    style={{
                      top: '-30px',
                      position: 'relative',
                      color: 'black',
                      font: 'normal 400 16px/18px Arial',
                    }}
                    primary={t(ctry.country, {
                      defaultValue: ctry.country,
                    })}
                  />
                </label>
              </div>
            </MenuItem>
          ))}
        </Select>
        {InternaltionalWireTransferError && (
          <span
            id="internaltionalWireTransferError_errorSpan"
            className={styleClass.error_message_product}
            role="alert"
          >
            {t(InternaltionalWireTransferError, {
              defaultValue: InternaltionalWireTransferError,
            })}
          </span>
        )}
      </div>
    </div>
  );
}
